import { doc, updateDoc, getDoc, addDoc, collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';

export interface ParentLoginSession {
  id?: string;
  parentId: string;
  loginTime: Timestamp;
  logoutTime?: Timestamp;
  duration?: number; // milliseconds
  ipAddress?: string;
  userAgent?: string;
  sessionId: string;
}

export interface ParentLoginStats {
  totalLogins: number;
  totalTimeSpent: number; // milliseconds
  firstLogin?: Timestamp;
  lastLogin?: Timestamp;
  averageSessionDuration: number;
  sessionsThisWeek: number;
  sessionsThisMonth: number;
}

export class ParentLoginTracker {
  private static currentSessionId: string | null = null;
  private static sessionStartTime: Date | null = null;
  private static recordingLogin = false;

  /**
   * Record when a parent logs in
   */
  static async recordLogin(parentId: string): Promise<string> {
    if (this.recordingLogin) {
      console.log('[ParentLoginTracker] Login already in progress, skipping duplicate');
      return '';
    }

    if (this.currentSessionId) {
      console.log('[ParentLoginTracker] Session already exists, skipping duplicate login');
      return '';
    }

    this.recordingLogin = true;

    try {
      const now = Timestamp.now();
      const sessionId = `parent_${parentId}_${Date.now()}`;

      // Create new login session record
      const loginSession: Omit<ParentLoginSession, 'id'> = {
        parentId,
        loginTime: now,
        sessionId,
        ipAddress: await this.getClientIP(),
        userAgent: navigator.userAgent
      };

      const docRef = await addDoc(collection(db, 'parentLoginSessions'), loginSession);

      // Best-effort: update parent's last login in their user record.
      // NOTE: Firestore rules may not allow parents to update these audit fields.
      // The session record above is the source of truth; this write is optional.
      try {
        const userRef = doc(db, 'users', parentId);
        await updateDoc(userRef, {
          lastLogin: now,
          lastLoginType: 'parent_dashboard'
        });
      } catch (e: any) {
        const code = String(e?.code || '');
        if (code === 'permission-denied') {
          console.warn('[ParentLoginTracker] Skipping user lastLogin update (permission denied).');
        } else {
          console.warn('[ParentLoginTracker] Skipping user lastLogin update (non-fatal error).', e);
        }
      }

      // Store session info for logout tracking
      this.currentSessionId = sessionId;
      this.sessionStartTime = new Date();

      console.log('[ParentLoginTracker] Login recorded for parent:', parentId, 'Session:', sessionId);

      return docRef.id;
    } catch (error) {
      console.error('[ParentLoginTracker] Error recording login:', error);
      throw error;
    } finally {
      this.recordingLogin = false;
    }
  }

  /**
   * Record when a parent logs out
   */
  static async recordLogout(parentId: string): Promise<void> {
    if (!this.currentSessionId || !this.sessionStartTime) {
      console.warn('[ParentLoginTracker] No active session to log out');
      return;
    }

    try {
      const now = Timestamp.now();
      const duration = Date.now() - this.sessionStartTime.getTime();

      // Find and update the session record
      const sessionsRef = collection(db, 'parentLoginSessions');
      const q = query(
        sessionsRef,
        where('sessionId', '==', this.currentSessionId),
        where('parentId', '==', parentId)
      );

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const sessionDoc = snapshot.docs[0];
        await updateDoc(sessionDoc.ref, {
          logoutTime: now,
          duration: duration
        });

        console.log('[ParentLoginTracker] Logout recorded for parent:', parentId, 'Duration:', Math.round(duration / 1000), 's');
      }

      // Clear session tracking
      this.currentSessionId = null;
      this.sessionStartTime = null;

    } catch (error) {
      console.error('[ParentLoginTracker] Error recording logout:', error);
    }
  }

  /**
   * Get comprehensive login statistics for a parent
   */
  static async getParentLoginStats(parentId: string): Promise<ParentLoginStats> {
    try {
      const sessionsRef = collection(db, 'parentLoginSessions');
      
      // First try the query with orderBy - this requires a composite index
      let sessions: ParentLoginSession[] = [];
      
      try {
        const q = query(
          sessionsRef,
          where('parentId', '==', parentId),
          orderBy('loginTime', 'desc')
        );
        const snapshot = await getDocs(q);
        sessions = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as ParentLoginSession[];
      } catch (indexError: any) {
        // If index doesn't exist, fall back to simple query and sort in memory
        console.warn('[ParentLoginTracker] Composite index not available, falling back to client-side sorting');
        const fallbackQuery = query(
          sessionsRef,
          where('parentId', '==', parentId)
        );
        const fallbackSnapshot = await getDocs(fallbackQuery);
        sessions = (fallbackSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as ParentLoginSession[])
          .sort((a: ParentLoginSession, b: ParentLoginSession) => b.loginTime.toMillis() - a.loginTime.toMillis());
      }

      // Calculate statistics
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const totalLogins = sessions.length;
      const totalTimeSpent = sessions
        .filter(s => s.duration)
        .reduce((total, session) => total + (session.duration || 0), 0);

      const averageSessionDuration = totalLogins > 0 ? totalTimeSpent / totalLogins : 0;

      const sessionsThisWeek = sessions.filter(s =>
        s.loginTime.toDate() >= oneWeekAgo
      ).length;

      const sessionsThisMonth = sessions.filter(s =>
        s.loginTime.toDate() >= oneMonthAgo
      ).length;

      const stats: ParentLoginStats = {
        totalLogins,
        totalTimeSpent,
        firstLogin: sessions.length > 0 ? sessions[sessions.length - 1].loginTime : undefined,
        lastLogin: sessions.length > 0 ? sessions[0].loginTime : undefined,
        averageSessionDuration,
        sessionsThisWeek,
        sessionsThisMonth
      };

      return stats;

    } catch (error) {
      console.error('[ParentLoginTracker] Error getting login stats:', error);
      return {
        totalLogins: 0,
        totalTimeSpent: 0,
        averageSessionDuration: 0,
        sessionsThisWeek: 0,
        sessionsThisMonth: 0
      };
    }
  }

  /**
   * Get all login sessions for a parent (for admin use)
   */
  static async getParentLoginHistory(parentId: string, limit: number = 50): Promise<ParentLoginSession[]> {
    try {
      const sessionsRef = collection(db, 'parentLoginSessions');
      
      try {
        const q = query(
          sessionsRef,
          where('parentId', '==', parentId),
          orderBy('loginTime', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.slice(0, limit).map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as ParentLoginSession[];
      } catch (indexError) {
        // Fall back to simple query and sort in memory
        console.warn('[ParentLoginTracker] Composite index not available for history, falling back to client-side sorting');
        const fallbackQuery = query(
          sessionsRef,
          where('parentId', '==', parentId)
        );
        const fallbackSnapshot = await getDocs(fallbackQuery);
        return (fallbackSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as ParentLoginSession[])
          .sort((a: ParentLoginSession, b: ParentLoginSession) => b.loginTime.toMillis() - a.loginTime.toMillis())
          .slice(0, limit);
      }

    } catch (error) {
      console.error('[ParentLoginTracker] Error getting login history:', error);
      return [];
    }
  }

  /**
   * Get parent login analytics for admin dashboard
   */
  static async getParentLoginAnalytics(): Promise<{
    totalParentLogins: number;
    uniqueParents: number;
    averageLoginsPerParent: number;
    loginsThisWeek: number;
    loginsThisMonth: number;
  }> {
    try {
      const sessionsRef = collection(db, 'parentLoginSessions');
      const snapshot = await getDocs(sessionsRef);

      const sessions = snapshot.docs.map(doc => doc.data()) as ParentLoginSession[];

      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const uniqueParents = new Set(sessions.map(s => s.parentId)).size;
      const totalParentLogins = sessions.length;
      const averageLoginsPerParent = uniqueParents > 0 ? totalParentLogins / uniqueParents : 0;

      const loginsThisWeek = sessions.filter(s =>
        s.loginTime.toDate() >= oneWeekAgo
      ).length;

      const loginsThisMonth = sessions.filter(s =>
        s.loginTime.toDate() >= oneMonthAgo
      ).length;

      return {
        totalParentLogins,
        uniqueParents,
        averageLoginsPerParent,
        loginsThisWeek,
        loginsThisMonth
      };

    } catch (error) {
      console.error('[ParentLoginTracker] Error getting analytics:', error);
      return {
        totalParentLogins: 0,
        uniqueParents: 0,
        averageLoginsPerParent: 0,
        loginsThisWeek: 0,
        loginsThisMonth: 0
      };
    }
  }

  /**
   * Setup automatic logout tracking on page unload
   */
  static setupAutoLogout(parentId: string): void {
    // Track when user closes browser/tab
    window.addEventListener('beforeunload', () => {
      this.recordLogout(parentId);
    });

    // Track when user becomes inactive (optional - could be useful for analytics)
    let inactiveTimeout: NodeJS.Timeout;
    const INACTIVE_TIME = 30 * 60 * 1000; // 30 minutes

    const resetInactivityTimer = () => {
      clearTimeout(inactiveTimeout);
      inactiveTimeout = setTimeout(() => {
        this.recordLogout(parentId);
      }, INACTIVE_TIME);
    };

    // Reset timer on user activity
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
      document.addEventListener(event, resetInactivityTimer, true);
    });

    resetInactivityTimer();
  }

  /**
   * Get client IP address (best effort)
   */
  private static async getClientIP(): Promise<string> {
    return 'unknown';
  }
}